import { Format, Immutable, NumberFormatter } from '@zervant/reactcomponents'
import { downloadXrechnungUrl } from 'client/api/invoice'
import { DeliverynoteInterface } from 'client/components/DocumentsV1/Deliverynote/DeliverynoteInterface'
import { ReceiptInterface } from 'client/components/DocumentsV1/Receipt/ReceiptInterface'
import { Segment } from 'client/Global/Segment'
import { SegmentEvents } from 'client/Global/SegmentEvents'
import { TradeNameInterface } from 'client/Interfaces/Settings/TradeNameInterface'
import { l } from 'client/Other/Translation'

import { getDocumentPDFUrl } from '../../api/document'
import { downloadFileFromURL } from '../../Global/Download'
import { DocumentType, DocumentTypes, DraftInterface } from '../../Interfaces/Draft/DraftInterface'
import { InvoiceInterface } from '../../Interfaces/Invoice/InvoiceInterface'
import { SettingsInterface } from '../../Interfaces/Settings/SettingsInterface'

// For invoice and creditnote
export function downloadDocumentPDF(invoice: InvoiceInterface) {
  const id = invoice?.id
  const documentType = invoice?.creditnotedocument ? DocumentTypes.CREDITNOTE : DocumentTypes.INVOICE
  const fileName = constructDocumentFileName(invoice?.invoicedocument || invoice?.creditnotedocument) as string
  const fullFileName = fileName?.endsWith('.pdf') ? fileName : `${fileName}.pdf`
  return downloadPDF(documentType, id, fullFileName)
}

export const downloadXRechnung = (invoice: InvoiceInterface) => {
  const documentType = invoice.creditnotedocument ? DocumentTypes.CREDITNOTE : DocumentTypes.INVOICE
  const fileName = constructDocumentFileName(invoice?.invoicedocument || invoice?.creditnotedocument)
  if (documentType == DocumentTypes.INVOICE) {
    Segment.track(SegmentEvents.invoice.downloaded, {
      invoice_id: invoice.id,
      file_type: 'xrechnung',
      context: 'draft_preview_page',
    })
  }
  return downloadFileFromURL(downloadXrechnungUrl(invoice?.id, documentType), `${fileName}.xml`)
}

export function downloadPDF(documentType: DocumentType, id: number, fileName: string) {
  if (documentType == DocumentTypes.INVOICE) {
    Segment.track(SegmentEvents.invoice.downloaded, { invoice_id: id, file_type: 'pdf', context: 'draft_preview_page' })
  }
  return downloadFileFromURL(getDocumentPDFUrl(documentType, id), fileName)
}

export function constructDocumentFileName(document: DraftInterface) {
  const format = document?.fields?.locale?.value && Format.fromLocale(document?.fields?.locale?.value)
  const currencyFormat =
    format && document?.fields?.currency?.value ? new NumberFormatter(format, document?.fields.currency?.value) : ''
  const docNumber = document?.fields?.creditnoteNumber
    ? document?.fields?.creditnoteNumber?.value
    : document?.fields?.invoiceNumber?.value

  if (document?.fields?.title?.value && docNumber) {
    const title = document?.fields?.title?.value.toUpperCase()
    const grossTotal =
      document?.summary && currencyFormat
        ? currencyFormat.render(document?.summary?.grossTotal)?.replace('â‚¬', 'EUR')
        : ''
    const tradeName = document?.invoicer?.tradeName?.name

    const getDocNumber = (initial, increment) => {
      const parts = initial.split('-')
      return String(
        parts.map((item, i) => (i === parts.length - 1 ? Number(item) + Number(increment) : item)).join('-'),
      )
    }

    if (['schedulesDifferentInvoices', 'downPaymentAdvanceInvoice'].includes(document?.order?.orderType)) {
      if (document.preview && document.preview.length) {
        return document?.order?.items.map((item, index) => {
          return (
            `${title} ${getDocNumber(docNumber, index)} - ${currencyFormat && currencyFormat.render(item.value)}` +
            ` (${index + 1}/${document?.order?.items.length})`
          )
        })
      } else {
        const { itemIndex } = document.order
        const currentItem = document.order.items[itemIndex]
        return (
          `${title} ${currentItem.referenceInvoiceNumber} - ` +
          `${currencyFormat && currencyFormat.render(currentItem.value)} ` +
          `(${Number(itemIndex) + 1}/${document?.order?.items.length})`
        )
      }
    } else {
      return `${title} ${docNumber} - ${tradeName} - ${grossTotal}`
    }
  } else {
    return l('invoice.DefaultTitle')
  }
}

export const constructDeliverynoteFileName = (
  document: DeliverynoteInterface,
  tradeName: TradeNameInterface,
): string => {
  const docNumber = document.deliverynoteNumber
  return `${document.title.toUpperCase()} ${docNumber} - ${tradeName.name || ''}`
}

export const constructReceiptFileName = (document: ReceiptInterface, tradeName: TradeNameInterface): string => {
  const format = Format.fromLocale(document.config?.fields?.locale?.value)
  const currencyFormat = new NumberFormatter(format, document.summary.foreignCurrency)
  const docNumber = document.receiptNumber

  const grossTotal = currencyFormat.render(document.summary.foreignGrossTotal).replace('â‚¬', 'EUR')
  return `${document.title?.toUpperCase() || '_'} ${docNumber} - ${tradeName?.name || ''} - ${grossTotal}.pdf`
}

export const consturctConvertedDocumentFileName = (
  typeName: string,
  tradeName: TradeNameInterface,
  invoiceNumber: string,
) => {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return `${typeName.toUpperCase()} ${invoiceNumber}- ${tradeName || ''}.pdf`
}

export const constructDocumentFileNameV1 = (document, defaultConfig?) => {
  const config = document.config ? document.config : defaultConfig
  const tradeName = document.company.tradeName.name ? document.company.tradeName.name.replace('/', '') : ''
  if (document.documentType === 'deliverynote' || document.deliverynoteNumber) {
    return constructDeliverynoteFileName(document, document.company.tradeName)
  }
  const format = Format.fromLocale(config.fields.locale.value)
  const currencyFormat = new NumberFormatter(format, document.summary.foreignCurrency)
  const { invoiceNumber, creditNoteNumber, receiptNumber, estimateNumber } = document
  const docNumber = invoiceNumber || creditNoteNumber || receiptNumber || estimateNumber
  const grossTotal = currencyFormat.render(document.summary.foreignGrossTotal).replace('â‚¬', 'EUR')
  const docTitle = creditNoteNumber ? l('ING.export.credit-note') : document.title
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  return `${docTitle?.toUpperCase()} ${docNumber} - ${tradeName} - ${grossTotal}`
}

export function constructPdfFileName(document, type, companyName) {
  return constructFileName(document, type, companyName, 'pdf')
}

export function constructFileName(document, type, companyName, extension) {
  let docNumber
  let grossTotal
  if (type === DocumentTypes.ESTIMATE) {
    docNumber = document?.estimateNumber
    grossTotal = `${document?.summary?.localGrossTotal} ${document?.summary?.localCurrency}`
  } else if (type !== DocumentTypes.CREDITNOTE && typeof document?.invoiceNumber === 'object') {
    docNumber = `${document?.invoiceNumber?.prefix}-${document?.invoiceNumber?.number}`
    grossTotal = `${document?.summary?.grossTotal} ${document?.summary?.currency}`
  } else {
    docNumber = type === DocumentTypes.CREDITNOTE ? document?.creditnoteNumber : document?.invoiceNumber
    grossTotal = `${document?.summary?.localGrossTotal} ${document?.summary?.localCurrency}`
  }
  const title = document?.title ? (document?.title as string) : l('invoice.DefaultTitle')
  return `${title?.toUpperCase()} ${docNumber} - ${companyName} - ${grossTotal}.${extension}`
}

export function getPaidDateFromStatus(status) {
  return status.paidDate || ''
}

export function handleForReceipt(origDoc) {
  const title = origDoc.translations.it_receipt
  return {
    ...origDoc,
    title,
    fields: {
      ...origDoc.fields,
      title: {
        ...origDoc.fields.title,
        value: title,
      },
    },
  }
}

export function convertToReceipt(origDoc, paidDate, settings: SettingsInterface) {
  const doc = Immutable.copy(origDoc.invoicedocument)
  const oldStyles = doc.styles
  const defaultStyles =
    settings.styles.invoice && settings.styles.invoice.default ? Immutable.copy(settings.styles.invoice.default) : null
  const [country] = doc.fields.template.value.split('.')
  const fields = {
    ...doc.fields,
    template: {
      ...doc.fields.template,
      value: `${country}.default`,
    },
    issueDate: {
      name: 'issueDate',
      visible: true,
      value: paidDate,
    },
  }
  return {
    ...doc,
    fields,
    styles: defaultStyles || oldStyles,
    document: 'receipt',
  }
}

export const isValidPaymentMethod = (pm) => {
  const { fields } = pm
  if (!fields) {
    return false
  }
  return Object.keys(fields).length > 0
}

export function convertToDeliveryNote(origDoc) {
  const doc = Immutable.copy(origDoc.invoicedocument)
  const { fields, invoicer: companyData, receiver: customerData, lines } = doc
  const { language, id: customer } = customerData
  const { invoiceNumber, issueDate, dueDate } = fields
  const specialTypes = ['description', 'title', 'subtotal']
  const invoiceLines = lines
    .filter((l) => !specialTypes.includes(l.type.toLowerCase()))
    .map((l) => {
      const { date } = l
      return {
        ...l,
        ...(date ? { date: new Date(date) } : {}),
      }
    })

  return {
    id: origDoc.id,
    invoiceNumber: invoiceNumber.value,
    issueDate: new Date(issueDate.value),
    dueDate: new Date(dueDate.value),
    customer,
    fields,
    language,
    companyData,
    customerData,
    invoiceLines,
  }
}
